// ABOUTME: Modal component for celebrating approved MakeItRight lists with child-friendly messaging
// ABOUTME: Reuses CelebrationModal visual pattern (Dialog, gradient, confetti) for MakeItRight approvals

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { Celebration, CheckCircle } from '@mui/icons-material';
import { ChoreListPeriodStatus } from '../types/api';
import { celebrationGold, celebrationGoldHover, decorativeGradients } from '../theme/tokens';

interface MakeItRightCelebrationModalProps {
  isOpen: boolean;
  approvedLists: ChoreListPeriodStatus[];
  childName: string;
  onClose: () => void;
}

const MakeItRightCelebrationModal: React.FC<MakeItRightCelebrationModalProps> = ({
  isOpen,
  approvedLists,
  childName,
  onClose,
}) => {
  const theme = useTheme();

  if (approvedLists.length === 0) return null;

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'visible',
          background: decorativeGradients.heroPurple,
          color: 'white',
        }
      }}
    >
      <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <Celebration sx={{ fontSize: 48, color: celebrationGold }} />
        </Box>
        <Typography variant="h4" component="span" sx={{ fontWeight: 600, mb: 1, display: 'block' }}>
          You Made It Right, {childName}!
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ textAlign: 'center', px: 3 }}>
        <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
          Great job saving the day!
        </Typography>

        {approvedLists.map((list) => (
          <Box
            key={list.id}
            sx={{
              backgroundColor: alpha(theme.palette.common.white, 0.1),
              borderRadius: 2,
              p: 2,
              mb: 2,
              backdropFilter: 'blur(10px)',
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              {list.title.replace(/^Make It Right:\s*/i, '')}
            </Typography>
            <List dense disablePadding>
              {list.items.map((item) => (
                <ListItem key={item.id} dense disablePadding sx={{ py: 0.25 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    {/* Icon glyph sizing via fontSize, not a Typography text override */}
                    <CheckCircle sx={{ color: celebrationGold, fontSize: 20 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={item.title}
                    primaryTypographyProps={{
                      variant: 'body2',
                      sx: { color: 'white', opacity: 0.9 },
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        ))}

        <Typography variant="body2" sx={{ opacity: 0.8, fontStyle: 'italic', mt: 1 }}>
          Everyone makes mistakes — what matters is making it right!
        </Typography>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'center', px: 3, pb: 3 }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            backgroundColor: celebrationGold,
            color: theme.palette.text.primary,
            fontWeight: 600,
            minWidth: 140,
            '&:hover': {
              backgroundColor: celebrationGoldHover,
            },
          }}
          startIcon={<Celebration />}
        >
          Awesome!
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MakeItRightCelebrationModal;
